import React, { useMemo } from 'react'
import { Container } from 'react-bootstrap'

import { INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE, IntegrationServiceId } from '../../../common/integrations'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  CONTACT_UPDATE_STEP,
  selectContactUpdateStep,
  setContactUpdateStep,
  setCreateNewCRMContact,
  setExternalId,
} from '../../../store/meetingSummary/contactUpdate'

import { User } from '../../../models/User'

import { UpdateExternalContactModalInitialization } from '../../UpdateExternalContact/UpdateExternalContactModalInitialization'
import { SegmentedExternalContactUpdateView } from '../../UpdateExternalContact/SegmentedExternalContactUpdateView'
import { ExternalDocumentContactUpdateProps, useInitializeContactChange } from '../../UpdateExternalContact/hooks'
import { UpdateExternalContactSelectStep } from '../../UpdateExternalContact/UpdateExternalContactSelectStep'
import {
  DefaultExternalContactUpdateViewV2,
  UpdateExternalContactModal,
  UpdateExternalContactModalFooter,
} from '../../UpdateExternalContact/UpdateExternalContactModal'

interface UpdateExternalDocumentContactModalProps {
  user: User
  serviceId: string
  fileId: string
  show: boolean
  onCancel: () => void
  onSave: () => void
}

export const UpdateExternalDocumentContactModal: React.FC<UpdateExternalDocumentContactModalProps> = ({ serviceId, fileId, user, show, onCancel, onSave }) => {
  const dispatch = useAppDispatch()

  const contactUpdateStep = useAppSelector(selectContactUpdateStep)

  const integration = user.integrations?.find((e) => e.serviceId === serviceId)
  if (integration) {
    dispatch(setContactUpdateStep(CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP))
    dispatch(setExternalId(integration.externalId))
    dispatch(setCreateNewCRMContact(false))
  }

  const BodyComponent = <UpdateExternalDocumentContactModalBody serviceId={serviceId} user={user} fileId={fileId} onCancel={onCancel} />

  const FooterComponent = <UpdateExternalDocumentContactModalFooter serviceId={serviceId} user={user} fileId={fileId} onCancel={onCancel} onSave={onSave} />

  return (
    <UpdateExternalContactModal
      user={user}
      serviceId={serviceId}
      show={show}
      onCancel={onCancel}
      BodyComponent={BodyComponent}
      FooterComponent={contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP ? FooterComponent : null}
    />
  )
}

interface UpdateExternalDocumentContactModalBodyProps {
  serviceId: string
  user: User
  fileId: string
  onCancel: () => void
}

const UpdateExternalDocumentContactModalBody: React.FC<UpdateExternalDocumentContactModalBodyProps> = ({ serviceId, user, fileId, onCancel }) => {
  return (
    <>
      <UpdateExternalContactModalInitialization serviceId={serviceId} />
      <UpdateExternalDocumentContactModalBodyContent serviceId={serviceId} user={user} fileId={fileId} onCancel={onCancel} />
    </>
  )
}

interface UpdateExternalDocumentContactModalBodyContentProps {
  serviceId: string
  user: User
  fileId: string
  onCancel: () => void
}

export const UpdateExternalDocumentContactModalBodyContent: React.FC<UpdateExternalDocumentContactModalBodyContentProps> = ({
  serviceId,
  user,
  fileId,
  onCancel,
}) => {
  const { trackingClient } = useTracking()
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)

  const handleKeySelected = (selectedKey: string) => {
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_FIELD_SELECTED,
      eventDetails: {
        fileId,
        selectedKey,
      },
    })
  }

  const updateProps: ExternalDocumentContactUpdateProps = useMemo(() => ({ type: 'document', fileId }), [fileId])

  useInitializeContactChange({
    serviceId,
    user,
    updateProps,
    onCancel,
  })

  const ExternalContactUpdateView = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId as IntegrationServiceId)
    ? SegmentedExternalContactUpdateView
    : DefaultExternalContactUpdateViewV2

  return (
    <Container className='d-flex flex-column zocks-gap-1.25 p-0'>
      <div>
        {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_SELECT_STEP ? (
          <>
            <UpdateExternalContactSelectStep serviceId={serviceId} onCancel={onCancel} user={user} />
          </>
        ) : null}
        {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP ? (
          <ExternalContactUpdateView serviceId={serviceId} contactId={user.id} onKeySelected={handleKeySelected} />
        ) : null}
      </div>
    </Container>
  )
}

interface UpdateExternalDocumentContactModalFooterProps {
  serviceId: string
  user: User
  fileId: string
  onCancel: () => void
  onSave: () => void
}

const UpdateExternalDocumentContactModalFooter: React.FC<UpdateExternalDocumentContactModalFooterProps> = ({ serviceId, user, fileId, onCancel, onSave }) => {
  const updateProps: ExternalDocumentContactUpdateProps = useMemo(() => ({ type: 'document', fileId }), [fileId])

  const { tracked } = useTracking()

  const handleSave = tracked(onSave, { eventName: TrackEvent.DOCUMENT_CONTACT_UPDATED, eventDetails: { targetSystem: serviceId } })

  return <UpdateExternalContactModalFooter serviceId={serviceId} user={user} updateProps={updateProps} onSave={handleSave} onCancel={onCancel} />
}
